/*
 * Structural tidy-up behind snippets/joah-visibility-head.liquid.
 *
 * Visibility is NOT this file's job: the head snippet's CSS owns that, and because its rule is a
 * document-level attribute rule it applies to innerHTML-inserted cards at insertion, with no flash
 * and no hook needed. This file only does what CSS cannot: collapsing the cell a hidden card sat
 * in, and a section whose grid is all JOAH, on engines without :has(); removing keen-slider slides
 * outright; and hiding a recommendation shelf whose every card turned out to be JOAH so a heading
 * is not left sitting over nothing.
 *
 * Loaded with defer, after global.js. Deferred scripts run after parsing and BEFORE
 * DOMContentLoaded, which is the window needed to beat the keen-slider constructor that
 * sections/kdo-product-carousel.liquid registers as a DOMContentLoaded handler.
 */
(function () {
  'use strict';

  var JOAH_SELECTOR = '[data-product-vendor="joah"]';
  var CELL_SELECTOR = 'li, .grid__item, .keen-slider__slide, .slider__slide';
  var SHELF_SELECTOR = 'product-recommendations, cart-recommendations, .cart-recommendations';
  var SECTION_SELECTOR = '.shopify-section:not(main)';
  var GRID_SELECTOR = 'ul.product-grid:not(#product-grid)';
  var OBSERVED_ATTRIBUTE = 'data-joah-observed';

  // Mirrors the gate assets/predictive-search.js applies: a stylist referral is on record
  // (refName) and that stylist hides JOAH (refJoahHide). The equivalent helpers in global.js are
  // closure-scoped rather than on window, so this is re-implemented instead of imported.
  // localStorage throws outright in locked-down privacy modes: fail open.
  function hidesJoah() {
    try {
      var refName = localStorage.getItem('refName');
      if (refName === null || refName.trim() === '') return false;

      return localStorage.getItem('refJoahHide') === 'true';
    } catch (error) {
      return false;
    }
  }

  // keen-slider is index-and-transform based and counts its children at construction, so a hidden
  // slide leaves a blank frame and a wrong dot count. Remove the slide instead. Everywhere else
  // the opposite holds: SliderComponent.initPages() in global.js filters on clientWidth > 0 and a
  // ResizeObserver re-runs it, so hiding is handled natively there.
  //
  // On a warm session this runs before sections/kdo-product-carousel.liquid constructs its slider
  // and nothing more is needed. On a cold one the flag arrives after construction, so the slider
  // has to re-read its slides: that section exposes its instance as container.keenSlider and
  // update() is what re-queries the slide list and rebuilds the track.
  function pruneKeenSlides(root) {
    var pruned = [];

    root.querySelectorAll(JOAH_SELECTOR).forEach(function (card) {
      var slide = card.closest('.keen-slider__slide');
      if (!slide) return;

      var container = slide.closest('.keen-slider');
      if (container && pruned.indexOf(container) === -1) pruned.push(container);
      slide.remove();
    });

    pruned.forEach(function (container) {
      if (container.keenSlider && typeof container.keenSlider.update === 'function') {
        container.keenSlider.update();
      }
    });
  }

  function collapseCells(root) {
    root.querySelectorAll(JOAH_SELECTOR).forEach(function (card) {
      var cell = card.closest(CELL_SELECTOR);
      if (cell) cell.style.display = 'none';
    });
  }

  function shelfIsAllJoah(shelf) {
    var items = shelf.querySelectorAll('.card-wrapper, .mini-cart__navigation > li');
    if (items.length === 0) return false;

    for (var index = 0; index < items.length; index++) {
      var item = items[index];
      if (!item.matches(JOAH_SELECTOR) && !item.querySelector(JOAH_SELECTOR)) return false;
    }

    return true;
  }

  // The lesson PR #63 paid for on the all-JOAH search case, applied to recommendation shelves:
  // hiding every card in a shelf leaves its heading stranded over an empty row.
  function collapseEmptyShelves(root) {
    var shelves = Array.prototype.slice.call(root.querySelectorAll(SHELF_SELECTOR));
    if (root.nodeType === Node.ELEMENT_NODE && root.matches(SHELF_SELECTOR)) shelves.push(root);

    shelves.forEach(function (shelf) {
      if (shelfIsAllJoah(shelf)) shelf.style.display = 'none';
    });
  }

  // Fallback for the section rule in snippets/joah-visibility-head.liquid on engines without
  // :has(), with the same test: at least one card in the section's product grid, every one of them
  // JOAH, and never a section holding #product-grid (the facet path owns that, PR #63).
  // SECTION_SELECTOR excludes main#MainContent, which also carries shopify-section
  // (layout/theme.liquid) and contains every section, so neither the querySelectorAll nor the
  // closest() in collapseAllJoahSections can ever return it.
  function sectionIsAllJoah(section) {
    if (section.querySelector('#product-grid')) return false;

    var cards = section.querySelectorAll(GRID_SELECTOR + ' .card-wrapper');
    if (cards.length === 0) return false;

    for (var index = 0; index < cards.length; index++) {
      if (!cards[index].matches(JOAH_SELECTOR)) return false;
    }

    return true;
  }

  function collapseAllJoahSections(root) {
    var sections = Array.prototype.slice.call(root.querySelectorAll(SECTION_SELECTOR));
    if (root.nodeType === Node.ELEMENT_NODE) {
      var ownSection = root.closest(SECTION_SELECTOR);
      if (ownSection) sections.push(ownSection);
    }

    sections.forEach(function (section) {
      if (sectionIsAllJoah(section)) section.style.display = 'none';
    });
  }

  // Cold-start counterpart of the parse-time script in snippets/joah-blocked-notice.liquid, which
  // puts joah-pdp-blocked on the notice's parent. Covers the PDP and an open quick view alike.
  function blockProductPages() {
    document.querySelectorAll('.joah-blocked-notice').forEach(function (notice) {
      if (notice.parentNode) notice.parentNode.classList.add('joah-pdp-blocked');
    });
  }

  function sweep(root) {
    pruneKeenSlides(root);
    collapseCells(root);
    collapseEmptyShelves(root);
    collapseAllJoahSections(root);
  }

  // product-recommendations and cart-recommendations fetch their own markup and replace their own
  // direct children exactly once each (both call observer.unobserve(this) in handleIntersection).
  // childList with subtree false is deliberate: sweep only removes keen slides (never a direct
  // child here) and sets style.display on descendants, the observed element or its enclosing
  // section, so it never adds or removes a DIRECT child of the observed node and self-retriggering
  // is structurally impossible rather than merely guarded.
  //
  // The hooks live here and not in global.js or cart-recommendations.js because both of those are
  // upstream-synced and rewritten by the pending 9.1.0 branch, which would lose the hook.
  function observeShelves() {
    document.querySelectorAll('product-recommendations, cart-recommendations').forEach(function (shelf) {
      if (shelf.hasAttribute(OBSERVED_ATTRIBUTE)) return;
      shelf.setAttribute(OBSERVED_ATTRIBUTE, '');

      new MutationObserver(function () {
        if (hidesJoah()) sweep(shelf);
      }).observe(shelf, { childList: true });
    });
  }

  function arm() {
    if (!hidesJoah()) return;

    sweep(document);
    observeShelves();
  }

  arm();

  // On cart update the mini-cart section is re-rendered by the Section Rendering API and
  // <cart-recommendations> is REPLACED, which detaches its observer. Re-arming re-attaches one to
  // whatever is there now; the OBSERVED_ATTRIBUTE marker keeps repeated updates from stacking
  // observers on an element that already has one.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, arm);
  }

  // Cold start: on a fresh session landing directly on a ?ref= URL, refJoahHide does not exist yet
  // when the head snippet runs, so its gate correctly fails open and the content paints.
  // assets/customer-ref-data.js dispatches this once the flags are written, which is the free hook
  // for a late pass and avoids re-implementing the pollLocalStorage in global.js.
  document.addEventListener('customer-ref-initialized', function () {
    if (!hidesJoah()) return;

    document.documentElement.classList.add('joah-hidden');
    blockProductPages();
    arm();
  });
})();
